const identity = <T>(x: T) => x;

const choose = <T>(arr: T[]) => {
  const len = arr.length;
  const idx = Math.floor(Math.random() * len);
  return arr[idx];
};

const isNil = <T>(val?: T | null): val is null | undefined =>
  val === null || isUndefined(val);

const isUndefined = <T>(val: T | undefined): val is undefined =>
  val === undefined;

const stripLines = (msg: string): string => {
  return msg
    .split("\n")
    .map((line) => line.trim())
    .join("\n");
};

const some = <T>(
  arr: T[],
  comparator: (val: T) => T | boolean | null | undefined = identity<T>,
) => {
  if (!arr) {
    return false;
  }
  return arr.some(comparator);
};

const shuffleInPlace = <T>(array: T[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const rnd = Math.random();
    const j = Math.floor((1.0 + i) * rnd);
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const shuffle = <T>(array: T[]) => {
  const dup = [...array];
  return shuffleInPlace(dup);
};

const sample = <T>(array: T[], numSamples: number) => {
  return shuffle(array).slice(0, numSamples);
};

// Found these on stack overflow: https://stackoverflow.com/questions/69019873/how-can-i-get-typed-object-entries-and-object-fromentries-in-typescript
//
// Object.fromEntries
//
const typeSafeObjectFromEntries = <
  const T extends ReadonlyArray<readonly [PropertyKey, unknown]>,
>(
  entries: T,
): { [K in T[number] as K[0]]: K[1] } => {
  return Object.fromEntries(entries) as { [K in T[number] as K[0]]: K[1] };
};

//
// Object.entries
// (add const param for less broader types (ie. string -> "apple") -> const T extends Record<PropertyKey, unknown>)
//
const typeSafeObjectEntries = <T extends Record<PropertyKey, unknown>>(
  obj: T,
): { [K in keyof T]: [K, T[K]] }[keyof T][] => {
  return Object.entries(obj) as { [K in keyof T]: [K, T[K]] }[keyof T][];
};

// Convert null/undefined to '' for Formik initial values
const prepareFormikInitialValues = <
  T extends Record<string, File | string | number | null | boolean | undefined>,
  V extends {
    [K in [keyof T, string | T[keyof T]] as K[0]]: K[1];
  },
>(
  obj: T,
): V => {
  return typeSafeObjectFromEntries(
    typeSafeObjectEntries(obj).map(([k, v]) => [k, isNil(v) ? "" : v]),
  ) as V;
};

type TruncateOptionsType = Partial<{
  len: number;
  postfix: string;
}>;
const truncate = (
  value: string | undefined | null,
  options: TruncateOptionsType = {},
): string => {
  if (!value) {
    return "";
  }

  const DEFAULT_OPTIONS = Object.freeze({ len: 40, postfix: "..." });
  const { len, postfix } = { ...DEFAULT_OPTIONS, ...options };
  if (value.length <= len) {
    return value;
  }

  const newLength = len - postfix.length;
  return value.substring(0, newLength) + postfix;
};

export {
  choose,
  identity,
  isNil,
  isUndefined,
  prepareFormikInitialValues,
  sample,
  shuffle,
  some,
  stripLines,
  truncate,
  typeSafeObjectEntries,
  typeSafeObjectFromEntries,
};
